// Phase 5Z.12 — Header Notification Bell (pending profile requests + portal time-entries)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, UserCheck, Clock, Pencil, ClipboardList } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionsContext";

const POLL_MS = 60_000;

export default function NotificationBell() {
  const { can } = usePermissions();
  const canApproveProfile = can("profilanfragen", "approve");
  const canApproveOps = can("operations", "approve");
  const [pendingProfile, setPendingProfile] = useState(0);
  const [pendingPortalTimes, setPendingPortalTimes] = useState(0);
  const [pendingTimeChange, setPendingTimeChange] = useState(0);
  const [tasksUnclaimed, setTasksUnclaimed] = useState(0);
  const [tasksMine, setTasksMine] = useState(0);
  const [appUnread, setAppUnread] = useState(0);
  const [open, setOpen] = useState(false);

  async function load() {
    if (canApproveProfile) {
      try {
        const { data } = await api.get("/profile-change-requests/_summary");
        setPendingProfile(data?.pending || 0);
      } catch { setPendingProfile(0); }
    } else {
      setPendingProfile(0);
    }
    if (canApproveOps) {
      try {
        const { data } = await api.get("/time-entries", { params: { status: "offen" } });
        const n = (data || []).filter((t) => t.submitted_via_portal).length;
        setPendingPortalTimes(n);
      } catch { setPendingPortalTimes(0); }
      try {
        const { data } = await api.get("/zeit-change-requests/_summary");
        setPendingTimeChange(data?.pending || 0);
      } catch { setPendingTimeChange(0); }
    } else {
      setPendingPortalTimes(0);
      setPendingTimeChange(0);
    }
    // Tasks summary — visible to anyone (dept-scoped server-side)
    try {
      const { data } = await api.get("/tasks/_summary");
      setTasksUnclaimed(data?.unclaimed_office_open || 0);
      setTasksMine(data?.mine_open || 0);
    } catch { setTasksUnclaimed(0); setTasksMine(0); }
    // In-app unread counter (Phase 30)
    try {
      const { data } = await api.get("/notifications/me/_summary");
      setAppUnread(data?.unread || 0);
    } catch { setAppUnread(0); }
  }

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    function onClick(e) {
      if (!e.target.closest?.("[data-notif-root]")) setOpen(false);
    }
    window.addEventListener("click", onClick);
    return () => { clearInterval(id); window.removeEventListener("click", onClick); };
    // eslint-disable-next-line
  }, [canApproveProfile, canApproveOps]);

  const total = pendingProfile + pendingPortalTimes + pendingTimeChange + tasksUnclaimed + tasksMine + appUnread;
  if (!canApproveProfile && !canApproveOps && total === 0) {
    // still render so tasks/notifications can show up; only hide for users with no signals at all
    return (
      <div className="relative" data-notif-root data-testid="notification-bell-root">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="notification-bell-btn"
          className="relative p-2 rounded text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
        >
          <Bell className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative" data-notif-root data-testid="notification-bell-root">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell-btn"
        title={total > 0 ? `${total} offene Aufgabe(n)` : "Keine neuen Benachrichtigungen"}
        className={`relative p-2 rounded transition-colors ${total > 0 ? "text-amber-400 hover:bg-zinc-900" : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"}`}
      >
        <Bell className="w-4 h-4" />
        {total > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center leading-none"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notification-bell-menu"
          className="absolute right-0 mt-2 w-72 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Benachrichtigungen</span>
            {total === 0 && <span className="text-[10px] text-zinc-600">Alles erledigt</span>}
          </div>
          <div className="divide-y divide-zinc-800/60">
            {canApproveProfile && (
              <NotifRow
                to="/profilanfragen"
                icon={UserCheck}
                label="Offene Profilanfragen"
                hint={pendingProfile > 0 ? "Fahrer warten auf Freigabe" : "Keine offenen Anfragen"}
                count={pendingProfile}
                onClose={() => setOpen(false)}
                testid="notif-row-profile"
              />
            )}
            {canApproveOps && (
              <NotifRow
                to="/operations/zeiterfassung?status=offen&portal=1"
                icon={Clock}
                label="Portal-Zeiterfassungen"
                hint={pendingPortalTimes > 0 ? "Vom Fahrer-Portal eingereicht" : "Keine offenen Einträge"}
                count={pendingPortalTimes}
                onClose={() => setOpen(false)}
                testid="notif-row-times"
              />
            )}
            {canApproveOps && (
              <NotifRow
                to="/zeit-anfragen"
                icon={Pencil}
                label="Zeit-Änderungsanträge"
                hint={pendingTimeChange > 0 ? "Fahrer warten auf Korrektur-Freigabe" : "Keine offenen Anträge"}
                count={pendingTimeChange}
                onClose={() => setOpen(false)}
                testid="notif-row-time-change"
              />
            )}
            <NotifRow
              to="/operations/aufgaben"
              icon={ClipboardList}
              label="Offene Aufgaben (Office, frei)"
              hint={tasksUnclaimed > 0 ? "Niemand hat sie übernommen" : "Alle übernommen"}
              count={tasksUnclaimed}
              onClose={() => setOpen(false)}
              testid="notif-row-tasks-unclaimed"
            />
            <NotifRow
              to="/operations/aufgaben"
              icon={ClipboardList}
              label="Meine Aufgaben"
              hint={tasksMine > 0 ? "Du arbeitest an diesen Aufgaben" : "Aktuell nichts übernommen"}
              count={tasksMine}
              onClose={() => setOpen(false)}
              testid="notif-row-tasks-mine"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ to, icon: Icon, label, hint, count, onClose, testid }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      data-testid={testid}
      className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-800/60 transition-colors"
    >
      <div className={`p-2 rounded ${count > 0 ? "bg-amber-500/15 text-amber-400" : "bg-zinc-800 text-zinc-500"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-100 leading-tight">{label}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{hint}</div>
      </div>
      {count > 0 && (
        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500/20 text-red-400 border border-red-500/30">
          {count}
        </span>
      )}
    </Link>
  );
}
